import { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseconfig';
import { FaSearch, FaShoppingCart, FaFilter } from 'react-icons/fa';

const SpareParts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCart, setShowCart] = useState(false);

  // Fetch spare parts
  useEffect(() => {
    const fetchSpareParts = async () => {
      try {
        setLoading(true);
        const sparePartsQuery = query(collection(db, 'spareParts'));
        const snapshot = await getDocs(sparePartsQuery);
        const fetchedParts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setParts(fetchedParts);
      } catch (err) {
        console.error('Error fetching spare parts:', err);
        setError('Failed to load spare parts');
      } finally {
        setLoading(false);
      }
    };

    fetchSpareParts();
  }, []);

  const addToCart = (part) => {
    setCart([...cart, part]);
  };

  const removeFromCart = (partId) => {
    setCart(cart.filter(item => item.id !== partId));
  };

  const filteredParts = parts.filter(part => {
    const matchesSearch = part.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || part.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...new Set(parts.map(part => part.category))];

  if (loading) return <div>Loading spare parts...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header with search and filters */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8">
        <h1 className="text-3xl font-bold mb-4 md:mb-0">Spare Parts Catalog</h1>
        
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search parts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>

          <div className="relative">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
            <FaFilter className="absolute left-3 top-3 text-gray-400" />
          </div>

          <button
            onClick={() => setShowCart(!showCart)}
            className="relative bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <FaShoppingCart className="mr-2" />
            Cart ({cart.length})
          </button>
        </div>
      </div>

      {/* Cart sidebar */}
      {showCart && (
        <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-lg p-4 overflow-y-auto">
          <h2 className="text-xl font-bold mb-4">Shopping Cart</h2>
          {cart.length === 0 ? (
            <p>Your cart is empty</p>
          ) : (
            <>
              {cart.map(item => (
                <div key={item.id} className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium">{item.name}</h3>
                    <p className="text-gray-600">LKR{item.price}</p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t">
                <p className="font-bold">
                  Total: LKR{cart.reduce((sum, item) => sum + item.price, 0).toFixed(2)}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Parts grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredParts.map(part => (
          <div key={part.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
            <img
              src={part.image}
              alt={part.name}
              className="w-full h-48 object-contain mb-4"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'default-part-image.jpg';
              }}
            />
            <h3 className="text-lg font-semibold mb-2">{part.name}</h3>
            <p className="text-gray-600 mb-2">Category: {part.category}</p>
            <p className="text-gray-600 mb-2">Price: LKR{part.price}</p>
            <p className="text-gray-600 mb-2">Stock: {part.stock}</p>
            <p className="text-gray-600 mb-4">
              Compatible with: {part.compatibility.join(', ')}
            </p>
            <button
              onClick={() => addToCart(part)}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              disabled={part.stock === 0}
            >
              {part.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpareParts;